import type { Produto } from 'config/produto-env';
import type { ActivePhases } from 'hooks/useControlDate';
import type { PerfilStatus, Pessoa } from 'types/pessoa';
import { normalizeCategoryPremio } from 'utils/iniciativa-categoria';
import { CategoriaPreInscricao, getCategoriasPermitidas, hasPapel } from 'utils/pessoa';

/**
 * Decide a tela de entrada (`/`) a partir da base de pessoas e das fases.
 * Função pura: o `InitialRedirect` só busca os dados e navega.
 */

export const ROTA_PERFIL_INDISPONIVEL = '/perfil-indisponivel';

/** Rota única da pré-inscrição (Negócios, Pilar e ELBB); a categoria é resolvida na página. */
export const ROTA_PRE_INSCRICAO = '/pre-inscricao';

export type FasesDestino = Pick<
  ActivePhases,
  | 'conexaoPreRegistration'
  | 'conexaoRegistrationPeriod'
  | 'conexaoManagerApproval'
  | 'etapa1premio'
  | 'etapa2premio'
  | 'etapa3premio'
  | 'elbbPreRegistration'
>;

export interface IniciativaResumo {
  owner?: string;
  categoryPremio?: string;
}

export interface EntradaDestino {
  perfil: PerfilStatus | undefined;
  pessoa: Pessoa | null | undefined;
  fases: FasesDestino;
  produto: Produto;
}

/** A pré-inscrição do produto está aberta? */
export function isPreInscricaoAberta(fases: FasesDestino, produto: Produto): boolean {
  return produto === 'PREMIO_ELBB' ? fases.elbbPreRegistration : fases.conexaoPreRegistration;
}

/** A listagem abre com a pré-inscrição aberta e, no Prêmio BB, durante a aprovação do gestor. */
export function podeAcessarListagemPreInscricao(fases: FasesDestino, produto: Produto): boolean {
  return isPreInscricaoAberta(fases, produto) || (produto === 'PREMIO_BB' && fases.conexaoManagerApproval);
}

/** O item do menu é só para quem pode se pré-inscrever, com a fase aberta. */
export function deveMostrarMenuPreInscricao(
  pessoa: Pessoa | null | undefined,
  fases: FasesDestino,
  produto: Produto
): boolean {
  return isPreInscricaoAberta(fases, produto) && getCategoriasPermitidas(pessoa, produto).length > 0;
}

/** Situação da pessoa na pré-inscrição aberta; decide a notificação da home. */
export type SituacaoPreInscricao = 'pode_iniciar' | 'ja_inscrito';

export interface EntradaSituacaoPreInscricao {
  pessoa: Pessoa | null | undefined;
  fases: FasesDestino;
  produto: Produto;
  iniciativas: IniciativaResumo[];
}

/** `null` quando a fase está fechada ou a pessoa não pode se pré-inscrever em nenhuma categoria. */
export function getSituacaoPreInscricao({
  pessoa,
  fases,
  produto,
  iniciativas,
}: EntradaSituacaoPreInscricao): SituacaoPreInscricao | null {
  if (!pessoa || !isPreInscricaoAberta(fases, produto)) return null;

  const categorias = getCategoriasPermitidas(pessoa, produto);
  if (categorias.length === 0) return null;

  const temIniciativa = iniciativas.some(
    (iniciativa) =>
      iniciativa.owner === pessoa._id &&
      categorias.includes(normalizeCategoryPremio(iniciativa.categoryPremio) as CategoriaPreInscricao)
  );
  return temIniciativa ? 'ja_inscrito' : 'pode_iniciar';
}

/** Na pré-inscrição todos entram pela home, que mostra a notificação conforme a situação da pessoa. */
export function resolverDestinoInicial({ perfil, pessoa, fases, produto }: EntradaDestino): string {
  const home = produto === 'PREMIO_ELBB' ? '/home-elbb' : '/home-public';

  if (perfil !== 'ok' || !pessoa) return ROTA_PERFIL_INDISPONIVEL;

  if (produto === 'PREMIO_BB') {
    if (fases.etapa2premio || fases.etapa3premio) return home;
    if (fases.conexaoRegistrationPeriod || fases.etapa1premio) return home;
    if (fases.conexaoManagerApproval) {
      return hasPapel(pessoa, 'VALIDADOR_PRE_INSCRICAO') ? home : '/listagem';
    }
  }

  return home;
}
